import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { PublicOnly, RequireOwner, RequireUser, SessionProvider } from './auth/session';
import { Layout } from './components/Layout';
import { AccountPage } from './pages/AccountPage';
import { AdminPage } from './pages/admin/AdminPage';
import { InvitePage } from './pages/InvitePage';
import { LegalPage } from './pages/LegalPage';
import { SignupPage } from './pages/SignupPage';
import { LoginPage } from './pages/LoginPage';
import { MachinesPage } from './pages/MachinesPage';
import { MachinePage } from './pages/machine/MachinePage';
import { RecipePage } from './pages/recipe/RecipePage';
import { NewRecipePage } from './pages/recipe/NewRecipePage';
import { ImportPage } from './pages/recipe/ImportPage';
import { ChatPage } from './pages/workflow/ChatPage';
import { WorkflowPage } from './pages/workflow/WorkflowPage';
import { ConsentPage } from './pages/oauth/ConsentPage';
import { SharedRecipePage } from './pages/recipe/SharedRecipePage';
import { RecipesPage } from './pages/RecipesPage';
import { GalleryPage } from './pages/GalleryPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { AboutPage } from './pages/AboutPage';
import { ConnectionsPage } from './pages/ConnectionsPage';
import { LibraryPage } from './pages/LibraryPage';
import { GalleryImagePage } from './pages/gallery/GalleryImagePage';
import { SetupPage } from './pages/SetupPage';

/** The Markdown renderer is only needed for a topic page — loaded when one opens (doc 44). */
const TopicPage = lazy(() => import('./pages/knowledge/TopicPage').then((m) => ({ default: m.TopicPage })));

/** Keeps an old address working: /recipes/abc → /workflows/abc, with the query. */
function MovedTo({ prefix }: { prefix: string }) {
  const { '*': rest = '' } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${prefix}${rest ? `/${rest}` : ''}${search}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/setup" element={<PublicOnly page="setup"><SetupPage /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly page="login"><LoginPage /></PublicOnly>} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/legal/:key" element={<LegalPage />} />
      <Route path="/signup" element={<PublicOnly page="signup"><SignupPage /></PublicOnly>} />
      <Route path="/oauth/authorize" element={<RequireUser><ConsentPage /></RequireUser>} />
      <Route element={<RequireUser><Layout /></RequireUser>}>
        <Route index element={<MachinesPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="machines/:id" element={<MachinePage />} />
        <Route path="workflows" element={<RecipesPage />} />
        <Route path="workflows/new" element={<NewRecipePage />} />
        <Route path="workflows/import" element={<ImportPage />} />
        <Route path="workflows/:id" element={<RecipePage />} />
        <Route path="claude" element={<WorkflowPage />} />
        <Route path="claude/:id" element={<ChatPage />} />
        {/* Addresses from before "recipes" became "workflows" (doc 41): push links, bookmarks. */}
        <Route path="recipes/*" element={<MovedTo prefix="/workflows" />} />
        <Route path="workflow/*" element={<MovedTo prefix="/claude" />} />
        <Route path="shared/:id" element={<SharedRecipePage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="gallery/:id" element={<GalleryImagePage />} />
        <Route path="knowledge" element={<KnowledgePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="knowledge/:id" element={<Suspense fallback={<p role="status">Loading…</p>}><TopicPage /></Suspense>} />
        <Route path="admin" element={<RequireOwner><AdminPage /></RequireOwner>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } } });

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <AppRoutes />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
